import { useTransactions } from '@features/workspace/views/banking/account/transactions/api';
import { FC, useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Box } from '@mui/material';
import { Loading } from '@features/workspace/components/layouts/v2/loading';
import TransactionsFilters from '@features/workspace/views/banking/account/transactions/components/transactions-filters';
import PaginationWithPageSize from '@features/workspace/components/controls/pagination';
import useDebounce from '@hooks/use-debounce';
import usePagination from '@hooks/use-pagination';
import moment from 'moment';
import NoTransactions from '@features/workspace/views/banking/account/transactions/components/no-transactions';
import TransactionsTable from '@features/workspace/views/banking/account/transactions/components/transactions-table';
import {
  selectActiveBankAccount,
  selectActiveCompany,
} from '@/features/workspace/store/selectors';
import ContentCard from '@/components/ui/ContentCard';

const BankingAccountTransactions: FC = () => {
  const company = useSelector(selectActiveCompany);
  const activeBankAccount = useSelector(selectActiveBankAccount);
  const { page, pageSize, handlePageSizeChange, handlePageChange } =
    usePagination();
  const [search, setSearch] = useState('');
  const [period, setPeriod] = useState<string | null>(null);
  const [amount, setAmount] = useState<{
    min: number | null;
    max: number | null;
  }>({ min: null, max: null });
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);

  const debouncedSearch = useDebounce(search, 500);

  const transactionDateRange = useMemo(() => {
    if (dateRange[0]) {
      const endOfDay = moment.utc(dateRange[1] || dateRange[0]).endOf('day');
      const now = moment.utc();

      return {
        transactionDateFrom: moment
          .utc(dateRange[0])
          .startOf('day')
          .toISOString(),
        transactionDateTo: endOfDay.isAfter(now)
          ? now.toISOString()
          : endOfDay.toISOString(),
      };
    }

    if (!period) return {};

    switch (period) {
      case 'today':
        return {
          transactionDateFrom: moment.utc().startOf('day').toISOString(),
          transactionDateTo: moment().toISOString(),
        };
      case 'this-week':
        return {
          transactionDateFrom: moment.utc().startOf('week').toISOString(),
          transactionDateTo: moment().toISOString(),
        };
      case 'this-month':
        return {
          transactionDateFrom: moment.utc().startOf('month').toISOString(),
          transactionDateTo: moment().toISOString(),
        };
      case 'last-3-months':
        return {
          transactionDateFrom: moment
            .utc()
            .subtract(3, 'months')
            .startOf('month')
            .toISOString(),
          transactionDateTo: moment().toISOString(),
        };
      default:
        return {};
    }
  }, [period, dateRange]);

  const { data, isLoading } = useTransactions({
    companyId: company?.id as string,
    bankAccountNumber: activeBankAccount!.bankAccountNumber,
    pageSize,
    page,
    ...(debouncedSearch && { reference: debouncedSearch }),
    ...(transactionDateRange.transactionDateFrom && {
      transactionDateFrom: transactionDateRange.transactionDateFrom,
    }),
    ...(transactionDateRange.transactionDateTo && {
      transactionDateTo: transactionDateRange.transactionDateTo,
    }),
    ...(amount.min !== null && { amountFrom: amount.min }),
    ...(amount.max !== null && { amountTo: amount.max }),
  });

  const transactionsData = data?.data ?? [];
  const paginationData = data?.paging ?? null;

  const pagesCount = useMemo(() => {
    return paginationData?.totalPages ?? 1;
  }, [paginationData, pageSize]);

  useEffect(() => {
    handlePageChange(1);
  }, [debouncedSearch, period, amount, dateRange]);

  const clearAllFilters = () => {
    setSearch('');
    setPeriod(null);
    setAmount({ min: null, max: null });
    setDateRange([null, null]);
  };

  const hasActiveFilters =
    !!period ||
    !!search ||
    amount.min !== null ||
    amount.max !== null ||
    dateRange[0] !== null;

  return (
    <Box display="flex" flexDirection="column" flexGrow={1} gap={4}>
      <ContentCard>
        <TransactionsFilters
          amount={amount}
          clearAllFilters={clearAllFilters}
          dateRange={dateRange}
          period={period}
          search={search}
          setAmount={setAmount}
          setDateRange={setDateRange}
          setPeriod={setPeriod}
          setSearch={setSearch}
        />
        {isLoading && <Loading />}
        {!isLoading && !!transactionsData.length && (
          <>
            <Box sx={{ mx: -3 }}>
              <TransactionsTable transactions={transactionsData} />
            </Box>
            <Box className="-mx-6 flex items-center justify-between px-6 py-3">
              <div className="text-body3 text-navy-600">
                {/* {paginationData?.totalCount} items */}
              </div>
              <div>
                <PaginationWithPageSize
                  count={pagesCount}
                  hasPageSizeSelector={false}
                  page={page}
                  pageSize={pageSize}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                />
              </div>
            </Box>
          </>
        )}
        {!isLoading && !transactionsData.length && (
          <NoTransactions
            clearAllFilters={clearAllFilters}
            hasFilters={hasActiveFilters}
          />
        )}
      </ContentCard>
    </Box>
  );
};

export default BankingAccountTransactions;
